import { useEffect, useReducer, useState } from "react";
import { Link } from "react-router-dom";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../components/Table";
import Header from "../../components/Header";
import TableLayout from "../../components/layout/TableLayout";
import { Tooltip } from "@mui/material";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import LogoutIcon from "@mui/icons-material/Logout";
import CheckOutlinedIcon from "@mui/icons-material/CheckOutlined";
import CloseOutlinedIcon from "@mui/icons-material/CloseOutlined";
import { getSubmittedResignationData } from "../../services/exitManagement";
import useLoader from "../../hooks/useLoader";
import useAuthUser from "../../hooks/useAuthUser";
import { defaultDateFormatter } from "../../Helper/functionHelper";
import useModal from "../../hooks/useModal";
import AcceptRejectResignationForm from "../../components/exit-management/ApplyRejectResignationForm";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import ScheduleOutlinedIcon from "@mui/icons-material/ScheduleOutlined";
import ScheduleRoundTable from "../../components/exit-management/ScheduleRoundTable";
import ManagersFilter from "../../components/exit-management/ManagersFilter";
import WithdrawResignation from "../../components/exit-management/WithdrawResignationForm";
import Pagination from "../../components/common/Pagination";
import UserInfoCell from "../../components/common/UserInfoCell";

// Initial state
const initialState = {
  data: [],
  pagination: {},
  withdrawalSettings: {},
  loading: false,
  error: null,
};

// Reducer function
function reducer(state, action) {
  switch (action.type) {
    case "FETCH_START":
      return { ...state, loading: true, error: null };
    case "FETCH_SUCCESS":
      return {
        ...state,
        loading: false,
        data: action.payload.data,
        pagination: action.payload.pagination,
        withdrawalSettings: action.payload.withdrawalSettings,
      };
    case "FETCH_ERROR":
      return { ...state, loading: false, error: action.payload };
    default:
      return state;
  }
}

const ExitManagement = () => {
  const { loggedInUser } = useAuthUser();
  const { showLoader, hideLoader } = useLoader();
  const [page, setPage] = useState(1);
  const [state, dispatch] = useReducer(reducer, initialState);
  const { showModal } = useModal();

  const { data, pagination, withdrawalSettings } = state;
  const userRole = loggedInUser?.role;
  const isEmployeeRole = userRole === "7";

  const fetchResignations = async (pageNo = 1) => {
    dispatch({ type: "FETCH_START" });
    showLoader();
    try {
      const { status, data } = await getSubmittedResignationData(pageNo);

      if (status === 200 && data.result) {
        const {
          data: resignations = [],
          pagination,
          resignationWithdrawalDetails = {},
        } = data.result;

        dispatch({
          type: "FETCH_SUCCESS",
          payload: {
            data: resignations,
            pagination,
            withdrawalSettings: resignationWithdrawalDetails,
          },
        });
      } else {
        dispatch({
          type: "FETCH_SUCCESS",
          payload: {
            data: [],
            pagination: {},
            withdrawalSettings: {},
          },
        });
      }
    } catch (err) {
      dispatch({
        type: "FETCH_ERROR",
        payload: err.message || "Unknown error",
      });
    } finally {
      hideLoader();
    }
  };

  useEffect(() => {
    fetchResignations(page);
    // eslint-disable-next-line
  }, [page]);

  const canWithdraw = (createdAt, status) => {
    const { resignationWithdrawalAllowed, resignationWithdrawalDays } =
      withdrawalSettings;

    if (!resignationWithdrawalAllowed) return false;

    const currentDate = new Date();
    currentDate.setHours(0, 0, 0, 0);

    const createdDate = new Date(createdAt);
    createdDate.setHours(0, 0, 0, 0);

    const daysDiff = Math.floor(
      (currentDate - createdDate) / (1000 * 60 * 60 * 24),
    );
    if (status === 0) return true;
    if (status === 1) return false;

    return (
      daysDiff <= resignationWithdrawalDays &&
      status !== 3 &&
      status !== 6 &&
      status !== 9
    );
  };

  const reasonMap = {
    other: "Other",
    relocation: "Relocation",
    personal: "Personal Reason",
    new_opportunity: "New Opportunity",
  };

  const handleModalOpen = (title, resignationId, action) => {
    let body = null;
    if (action === "accept") {
      body = (
        <AcceptRejectResignationForm
          resignationId={resignationId}
          action="accept"
          fetchResignations={() => fetchResignations(page)}
        />
      );
    } else if (action === "reject") {
      body = (
        <AcceptRejectResignationForm
          resignationId={resignationId}
          action="reject"
          fetchResignations={() => fetchResignations(page)}
        />
      );
    } else if (action === "schedule") {
      body = (
        <ScheduleRoundTable
          resignationId={resignationId}
          fetchResignations={() => fetchResignations(page)}
        />
      );
    } else if (action === "employee-withdraw") {
      body = (
        <WithdrawResignation
          resignationId={resignationId}
          action={action}
          fetchResignations={() => fetchResignations(page)}
        />
      );
    }
    showModal(title, body);
  };

  const handleFilterData = (filterData) => {
    dispatch({ type: "FETCH_START" });
    dispatch({
      type: "FETCH_SUCCESS",
      payload: {
        data: filterData.data || [],
        pagination: filterData.pagination || {},
        withdrawalSettings: filterData.withdrawalSettings || {},
      },
    });
  };
  const statusMap = {
    0: "Applied",
    1: "Withdrawn",
    2: "Accepted By Manager",
    3: "Rejected By Manager",
    4: "Round Table Scheduled By Manager",
    5: "Accepted By HR",
    6: "Rejected By HR",
    9: "Retained By HR",
  };

  return (
    <div className="overflow-hidden">
      <Header title="Exit Management">
        {isEmployeeRole && (
          <span className="add float-end">
            <Link
              to="/exit-management/apply-resignation"
              className="btn btn-success btn-sm"
            >
              <span className="material-icons-outlined">add_box</span>&nbsp;
              <span className="text">Request</span>
            </Link>
          </span>
        )}
      </Header>
      {!isEmployeeRole && (
        <ManagersFilter
          updateFilteredData={handleFilterData}
          fetchResignations={fetchResignations}
        />
      )}
      <TableLayout>
        <Table className="table-col-freeze">
          <TableHeader>
            <TableRow>
              {!isEmployeeRole && (
                <>
                  <TableHead className="last-col-freeze">
                    Employee Name (Employee Id)
                  </TableHead>
                  <TableHead>Reporting Manager</TableHead>
                  <TableHead>Department</TableHead>
                  <TableHead>Designation</TableHead>
                </>
              )}
              <TableHead>Notice Period (in days)</TableHead>
              <TableHead>Applied Date</TableHead>
              <TableHead>Actual Last Working Day</TableHead>
              <TableHead>Desired Last Working Day</TableHead>
              <TableHead>Reason</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="last-col-freeze">Action</TableHead>
            </TableRow>
          </TableHeader>

          <TableBody>
            {data.length > 0 ? (
              data.map((item) => {
                return (
                  <TableRow key={item._id}>
                    {!isEmployeeRole && (
                      <>
                        <TableCell>
                          <UserInfoCell
                            employeeId={item.userEmployeeId}
                            name={item.userName}
                            token={item.userToken}
                          />
                        </TableCell>
                        <TableCell>{item.reportingManagerName}</TableCell>
                        <TableCell>{item.departmentName}</TableCell>
                        <TableCell>{item.designationName}</TableCell>
                      </>
                    )}
                    <TableCell>{item.noticePeriodDays}</TableCell>
                    <TableCell>
                      {defaultDateFormatter(item.createdAt)}
                    </TableCell>
                    <TableCell>
                      {defaultDateFormatter(item.actualLastWorkingDay)}
                    </TableCell>
                    <TableCell>
                      {defaultDateFormatter(item.desiredLastWorkingDay)}
                    </TableCell>
                    <TableCell>
                      {item.reason !== "other" && item.reason !== ""
                        ? reasonMap[item.reason]
                        : item.otherReason?.length > 50
                          ? item.otherReason.substring(0, 50) + "..."
                          : item.otherReason || "--"}
                      {item.reason !== "" &&
                        item.reason === "other" &&
                        item.otherReason?.length > 50 && (
                          <Tooltip title="Read more" placement="top" arrow>
                            <InfoOutlinedIcon
                              onClick={() => {
                                showModal(
                                  "Reason",
                                  <p style={{ wordWrap: "break-word" }}>
                                    {item.otherReason}
                                  </p>,
                                );
                              }}
                            />
                          </Tooltip>
                        )}
                    </TableCell>
                    <TableCell>{statusMap[item.status]}</TableCell>
                    <TableCell className="last-col-freeze">
                      <Tooltip title="View Details" placement="top" arrow>
                        <Link
                          className="text-decoration-none text-dark"
                          to={`/exit-management/view-resignation/${item._id}`}
                        >
                          <VisibilityOutlinedIcon />
                        </Link>
                      </Tooltip>
                      {canWithdraw(item.createdAt, item.status) &&
                        isEmployeeRole && (
                          <Tooltip
                            title="Withdraw Resignation"
                            placement="top"
                            arrow
                          >
                            <LogoutIcon
                              className="cursor-pointer"
                              onClick={() => {
                                handleModalOpen(
                                  "Withdraw Resignation",
                                  item._id,
                                  "employee-withdraw",
                                );
                              }}
                            />
                          </Tooltip>
                        )}
                      {!isEmployeeRole && (
                        <>
                          {(item.status === 0 || item.status === 4) && (
                            <>
                              <Tooltip
                                title="Accept Resignation"
                                placement="top"
                                arrow
                              >
                                <CheckOutlinedIcon
                                  onClick={() => {
                                    handleModalOpen(
                                      "Accept Resignation",
                                      item._id,
                                      "accept",
                                    );
                                  }}
                                  className="cursor-pointer"
                                />
                              </Tooltip>
                              <Tooltip
                                title="Reject Resignation"
                                placement="top"
                                arrow
                              >
                                <CloseOutlinedIcon
                                  onClick={() => {
                                    handleModalOpen(
                                      "Reject Resignation",
                                      item._id,
                                      "reject",
                                    );
                                  }}
                                  className="cursor-pointer"
                                />
                              </Tooltip>
                            </>
                          )}
                          {item.status === 0 && (
                            <Tooltip
                              title="Schedule Round Table"
                              placement="top"
                              arrow
                            >
                              <ScheduleOutlinedIcon
                                onClick={() => {
                                  handleModalOpen(
                                    "Schedule Round Table Discussion",
                                    item._id,
                                    "schedule",
                                  );
                                }}
                                className="cursor-pointer"
                              />
                            </Tooltip>
                          )}
                        </>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })
            ) : (
              <TableRow>
                <TableCell colSpan="7" className="text-center">
                  No records found
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableLayout>

      {pagination && data.length > 0 && (
        <Pagination
          totalPages={pagination.totalPages}
          currentPage={Number(pagination.pageNo)}
          perPage={pagination.perPage}
          totalRecords={pagination.totalRecords}
          onPageChange={setPage}
        />
      )}
    </div>
  );
};

export default ExitManagement;
